import React, { useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import Inspect from 'inspx';
import 'scss/main';

import { setInterceptor } from 'client/Axios';
import { scrubSentryEvent } from 'client/sentryErrorReport';
import { useUser } from 'context/UserContext';
import useNetworkError from 'hooks/useNetworkError';
import { RESIZE_OBSERVER_LOOP_ERRORS } from 'utils/BenignBrowserErrors';
import { getENV } from 'utils/HelperUtils';
import { APP_ENV } from 'utils/appEnv';
import env from 'utils/env';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { isExpectedApiError } from 'utils/sentryExpectedApiErrors';

import MaintenanceTicker from 'components/shared/MaintenanceTicker';
import UnderMaintenance from 'pages/UnderMaintenance';

import DevTools from './components/dev-tools/DevTools';
import LoadingSplashScreen from './components/shared/LoadingSplashScreen';
import { ToastProvider } from './context/ToastContext';

const AuthenticatedApp = lazyWithRetry(
  () => import(/* webpackChunkName: "AuthenticatedApp" */ './AuthenticatedApp'),
  'authApp'
);

const UnAuthenticatedApp = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "UnAuthenticatedApp" */ './UnAuthenticatedApp'),
  'UnauthApp'
);

const ToastPortal = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "ToastPortal" */ 'components/design-system/toast-portal/ToastPortal'
    ),
  'toastPortal'
);
const TourProvider = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "TourContext" */ 'context/TourContext').then(
      (module) => ({ default: module.TourProvider })
    ),
  'TourProvider'
);

const PasswordExpiredPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "PasswordExpired" */ 'components/authentication/AuthPasswordExpired'
    ),
  'PasswordExpired'
);

Sentry.init({
  environment: APP_ENV,
  release: env.SENTRY_RELEASE,
  dsn: env.SENTRY_DSN,
  integrations: [new BrowserTracing()],
  ignoreErrors: [
    // Ignore Certain Error
    // Please provide a reason why it should be ignored

    // Probably Related to Grammarly, Nothing we can do about it
    "Identifier 'originalPrompt' has already been declared",

    // When user misspelled their email when signing in/ signing up
    'User not found',

    // User access non existent post by manipulating URL
    'Culture post not found',

    // Error when input incorrect organization identifier,
    // not worthy to be log
    'Organization not found',

    // Error because of throttling (login)
    // not worthy to be log
    'Too many request, try again later.',

    // Error because user input invalid old password when trying to reset password
    // not worthy to be log
    'Invalid Old Password',

    // According to https://stackoverflow.com/a/50387233
    // We can safely ignored this errors
    ...RESIZE_OBSERVER_LOOP_ERRORS,

    // Not yet supported mobile features
    "Cannot read property 'getReadModeConfig' of undefined",
    "Cannot read property 'getReadModeExtract' of undefined",
    "Cannot read property 'getReadModeRender' of undefined",

    // Error due to blank responses and user not submitting reviews until the review cycle ends
    'Assignment already out of date'
  ],
  tracesSampler: (samplingContext) => {
    if (
      samplingContext.location.pathname.includes('/reviews/') ||
      samplingContext.location.pathname.includes('/appraisals')
    ) {
      // Higher chance to log formal review errors
      return 0.7;
    }

    return 0.3;
  },
  beforeSend: sentryBeforeSend
});

function sentryBeforeSend(event) {
  if (isExpectedApiError(event) || location.host === 'localhost:5000') {
    // Escaping expected "Request not Allowed" errors and errors sentry from localhost
    return null;
  } else if (
    event?.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename ===
    `<anonymous>`
  ) {
    // ignore error from anonymous files
    return null;
  }
  return scrubSentryEvent(event);
}

const App = () => {
  const data = useUser();
  const [ready, setReady] = useState(false);
  const toastRef = useRef();
  const { globalHandler } = useNetworkError();
  // Hard maintenance (env) full-page blocks; the read-only DB flag (config.underMaintenance) keeps the
  // app usable but pauses writes behind a sticky ticker (MaintenanceTicker + client/Axios).
  const isHardMaintenance = getENV('ENABLE_MAINTENANCE_MODE') === '1';
  const isReadOnlyMaintenance = Boolean(
    data?.config?.underMaintenance &&
      window.location.host === data?.organization?.performanceHost
  );

  const isExpired = data?.passwordExpired ? (
    <PasswordExpiredPage />
  ) : (
    <AuthenticatedApp />
  );

  useEffect(() => {
    setInterceptor(globalHandler);
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ready ? (
    <React.Suspense fallback={<LoadingSplashScreen />}>
      {APP_ENV !== 'production' && !window.Cypress && <DevTools />}
      <TourProvider>
        <Inspect disabled={APP_ENV === 'production'}>
          {isReadOnlyMaintenance && !isHardMaintenance && <MaintenanceTicker />}
          {isHardMaintenance ? (
            <UnderMaintenance />
          ) : (
            !data?.isLoading &&
            (data?.user ? isExpired : <UnAuthenticatedApp />)
          )}
        </Inspect>
        <ToastPortal ref={toastRef} />
      </TourProvider>
    </React.Suspense>
  ) : null;
};

const WrappedApp = (props) => {
  return (
    <ToastProvider>
      <App {...props} />
    </ToastProvider>
  );
};

export default WrappedApp;
